<template>
    <Layout :localeData="localeData">
        <main
            :class="$style.main"
            data-interface-section="listing"
        >
            <div :class="$style.collection">
                <CardContainer
                    v-if="hero"
                    :src="hero.src"
                    :srcset="hero.srcset"
                    :sizes="hero.sizes"
                    :lazy="false"
                    preload
                    responsive
                    :alt="collectionInfo?.title"
                />
                <Typography
                    is="h1"
                    type="header"
                    size="xxl"
                    :responsive="false"
                    :class="$style.title_override"
                >
                    {{ collectionInfo?.title }}
                </Typography>
                <ol
                    v-if="games.length"
                    :class="[$style.snippets, $style.divided]"
                >
                    <li
                        v-for="(item, index) in items"
                        :key="item.game.hru"
                    >
                        <CollectionSnippet
                            :game="item.game"
                            :rank="item.rank"
                            :link="item.anchor"
                            :ctrContext="snippetCtrContexts[index]"
                        />
                    </li>
                </ol>
                <div
                    v-if="collectionInfo?.description"
                    :class="$style.divided"
                >
                    <SeoText
                        :content="collectionInfo.description"
                        :class="$style.seo_override"
                    />
                </div>
                <ol :class="$style.cards">
                    <li
                        v-for="(item, index) in items"
                        :key="item.game.hru"
                    >
                        <CollectionGameCard
                            :game="item.game"
                            :rank="item.rank"
                            :link="item.link"
                            :coverExpand="coverExpand"
                            :ctrContext="ctrContexts[index]"
                        />
                    </li>
                </ol>
            </div>

            <div
                v-if="!appStore.isAppEnv && collectionInfo?.seoText"
                :class="$style.seo"
            >
                <SeoText
                    :content="collectionInfo.seoText"
                    :class="$style.seo_override"
                />
            </div>

            <PromoCategories :class="$style.promo_override" />
        </main>
    </Layout>
</template>
<script setup lang="ts">
import { computed, onMounted, onServerPrefetch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import CardContainer from '@/components/CardContainer.vue'
import PromoCategories from '@/components/PromoCategories/PromoCategories.vue'
import SeoText from '@/components/SeoText/SeoText.vue'
import Typography from '@/components/Typography.vue'
import { useGetExternalUrl } from '@/composables/url-helpers'
import { useCtrListContexts } from '@/composables/use-ctr-context'
import { useTr } from '@/composables/use-tr'
import Layout from '@/layouts/Layout.vue'
import { useListingResponse } from '@/pages/Category/use-listing-response'
import { useListingSeo } from '@/pages/Category/use-listing-seo'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'

import CollectionGameCard from './components/CollectionGameCard.vue'
import CollectionSnippet from './components/CollectionSnippet.vue'
import { previewImage } from './preview-image'
import { useCollectionPageData } from './use-collection-page-data'

const COVER_EXPAND_AFTER_LOAD = 2000

const appStore = useAppStore()
const $route = useRoute()
const router = useRouter()
const tr = useTr()
const getExternalUrl = useGetExternalUrl()

const collectionHru = computed(() => $route.params.hru as string)
const { collectionInfo, games, redirect, fetchStatus, hasError, preload } = useCollectionPageData({
    hru: collectionHru,
})

const hero = computed(() => collectionInfo.value?.seoPicture && previewImage(collectionInfo.value.seoPicture))

const items = computed(() =>
    games.value.map((game, index) => {
        const to = { name: ROUTE_NAMES.GAME_PAGE, params: { game: game.hru } }
        return {
            game,
            rank: index + 1,
            link: appStore.isAppEnv ? { to } : { externalTo: getExternalUrl(to) },
            anchor: { to: { hash: `#${game.hru}` } },
        }
    }),
)

const ctrContexts = useCtrListContexts(
    () => games.value,
    () => ({ placement: 'general', dimension1: collectionInfo.value?.id }),
)
const snippetCtrContexts = useCtrListContexts(
    () => games.value,
    () => ({ placement: 'collection_toc', dimension1: collectionInfo.value?.id }),
)

const titles = computed(() => {
    const title = collectionInfo.value?.title || ''
    return {
        templateTitle: title,
        defaultMetaTitle: tr('categoryPage.title', { title }),
        defaultMetaDescription: tr('categoryPage.description', { title }),
    }
})

const breadcrumbs = computed(() => [
    {
        title: tr('categoryPage.main_title'),
        href: router.resolve({ name: ROUTE_NAMES.MAIN }).fullPath,
    },
    ...(collectionInfo.value?.pathCategories || []),
])

const { localeData } = useListingSeo({
    info: () => collectionInfo.value,
    totalPages: () => (games.value.length ? 1 : undefined),
    routeName: () => ROUTE_NAMES.COLLECTION,
    page: () => 1,
    titles: () => titles.value,
    breadcrumbs: () => breadcrumbs.value,
    games: () => games.value,
})

const { processResponse } = useListingResponse({
    redirect: () => redirect.value,
    hasError: () => hasError.value,
    fetchStatus: () => fetchStatus.value,
    isEmpty: () => games.value.length === 0,
})

onServerPrefetch(async () => {
    await preload()
    processResponse()
})

onMounted(async () => {
    await preload()
    processResponse()
})

const coverExpand = ref<number>()
onMounted(() => {
    const enable = () => {
        coverExpand.value = COVER_EXPAND_AFTER_LOAD
    }
    if (document.readyState === 'complete') {
        enable()
    } else {
        window.addEventListener('load', enable, { once: true })
    }
})
</script>
<style module>
.main {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.collection {
    width: 100%;
    max-width: 800px;
    margin-inline: auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.title_override {
    font-size: var(--font-size-header-m);
    text-align: center;
}

.seo_override p + p {
    margin-top: 12px;
}

.snippets,
.cards {
    margin: 0;
    padding: 0;
    list-style: none;
}

.snippets {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    padding-block: 16px;
}

.seo {
    width: 100%;
    max-width: 800px;
    margin-inline: auto;
    border-top: 1px solid var(--white-08);
    padding-top: 20px;
}

.seo_override {
    --color: var(--carbon-600);
}

.cards {
    display: flex;
    flex-direction: column;
    gap: 24px;
    /* cancels the last card's bottom padding, so the space below starts at its text */
    margin-bottom: -16px;
}

.promo_override {
    width: 100%;
    max-width: 800px;
    margin: 40px auto 0;
    border-top: 1px solid var(--white-08);
    padding-top: 60px;
    align-items: center;
    text-align: center;
    --promo-justify: center;
}

@media (width >= 640px) {
    .collection {
        gap: 32px;
    }

    .title_override {
        font-size: var(--font-size-header-xxl);
    }

    .seo_override {
        max-width: 600px;
        margin-inline: auto;
        text-align: center;
    }

    .divided {
        border-top: 1px solid var(--white-08);
        padding-top: 32px;
    }

    .snippets {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 20px 12px;
        padding-bottom: 0;
    }

    .cards {
        max-width: 600px;
        width: 100%;
        margin-inline: auto;
        gap: 16px;
    }
}

@media (--desktop-small-plus) {
    .main {
        gap: 32px;
        margin-top: 40px;
    }

    .seo {
        padding-top: 32px;
    }

    .promo_override {
        margin-top: 24px;
        padding: 56px 0;
    }
}
</style>
